import React, { useState, useEffect, useCallback } from 'react';
import { Bookmark, Save, RotateCcw, Trash2, FolderOpen } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { getPresets, savePreset, deletePreset } from './materialApi';

/**
 * ConfigPresetsBar — benannte Feldkonfigurations-Presets + „Auf Standard
 * zurücksetzen". Presets sind serverseitige Snapshots; „Laden" wendet die
 * Konfiguration LOKAL an (Vorschau), gespeichert wird über den normalen Save-Button.
 *
 * Props:
 *   user, fields (aktuelle lokale Feldliste), showSuccess, showError
 *   onApplyConfig(configArray)  — Eltern wendet [{field_key, enabled, required}] lokal an
 *   onResetDefaults()           — Eltern setzt lokale Felder auf Katalog-Default
 */
const ConfigPresetsBar = ({ user, fields, onApplyConfig, onResetDefaults, showSuccess, showError }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [presets, setPresets] = useState([]);
  const [selId, setSelId] = useState('');

  const load = useCallback(async () => {
    try { const r = await getPresets(user); setPresets(r.presets || []); }
    catch { /* still */ }
  }, [user]);
  useEffect(() => { load(); }, [load]);

  const currentConfig = () => (fields || [])
    .filter((f) => !f.core)
    .map((f) => ({ field_key: f.key, enabled: !!f.enabled, required: !!f.required, sap_visible: !!f.sapVisible }));

  const onSave = async () => {
    const name = window.prompt(t('material.presetNamePrompt'));
    if (!name || !name.trim()) return;
    try {
      await savePreset(user, name.trim(), currentConfig());
      showSuccess?.(t('material.presetSaved'));
      load();
    } catch (e) { showError?.(e.message || t('material.presetSaveError')); }
  };

  const onLoad = () => {
    const p = presets.find((x) => String(x.id) === String(selId));
    if (!p) return;
    onApplyConfig?.(p.config || []);
    showSuccess?.(t('material.presetApplied', { name: p.name }));
  };

  const onDelete = async () => {
    const p = presets.find((x) => String(x.id) === String(selId));
    if (!p) return;
    if (!window.confirm(t('material.presetConfirmDelete', { name: p.name }))) return;
    try { await deletePreset(user, p.id); setSelId(''); showSuccess?.(t('material.presetDeleted')); load(); }
    catch { showError?.(t('material.presetDeleteError')); }
  };

  const inputBase = isDarkMode ? 'bg-white/5 border-white/15 text-white' : 'bg-white border-slate-300 text-slate-900';
  const btn = isDarkMode
    ? 'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-white/80 hover:bg-white/10 border border-white/10'
    : 'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-slate-600 hover:bg-slate-100 border border-slate-200';

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className={`inline-flex items-center gap-1 text-xs ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`}>
        <Bookmark className="w-3.5 h-3.5" /> {t('material.presets')}:
      </span>
      <select value={selId} onChange={(e) => setSelId(e.target.value)}
        className={`rounded-lg border px-2 py-2 text-xs outline-none ${inputBase}`}>
        <option value="">{t('material.presetChoose')}</option>
        {presets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
      </select>
      <button type="button" className={btn} disabled={!selId} onClick={onLoad}><FolderOpen className="w-3.5 h-3.5" /> {t('material.presetLoad')}</button>
      <button type="button" className={btn} disabled={!selId} onClick={onDelete}><Trash2 className="w-3.5 h-3.5" /> {t('material.delete')}</button>
      <span className={`mx-1 ${isDarkMode ? 'text-white/15' : 'text-slate-200'}`}>|</span>
      <button type="button" className={btn} onClick={onSave}><Save className="w-3.5 h-3.5" /> {t('material.presetSaveAs')}</button>
      <button type="button" className={btn} onClick={onResetDefaults}><RotateCcw className="w-3.5 h-3.5" /> {t('material.presetReset')}</button>
    </div>
  );
};

export default ConfigPresetsBar;
